import { ReactNode, isValidElement } from 'react';
import { createRoot } from 'react-dom/client';

import { grey9 } from 'approot/shared/styles/colours.style';
import { TOOLTIP_ID, TOOLTIP_MARGIN } from './tooltip.constants';
import { TOOLTIP_TAIL_OFFSET, TOOLTIP_TAIL_WIDTH } from './tooltip.style';

type Props = {
  isTooltipOnRight: boolean;
  isTooltipOnLeft: boolean;
  isTooltipBelow: boolean;
};

function getTooltipTailPosition({
  isTooltipOnRight,
  isTooltipOnLeft,
  isTooltipBelow,
}: Props) {
  const positionPrefix = isTooltipBelow ? 'top' : 'bottom';

  if (isTooltipOnLeft) return `${positionPrefix}-right`;
  if (isTooltipOnRight) return `${positionPrefix}-left`;

  return `${positionPrefix}-center`;
}

export const getTooltipPosition = (
  associatedElement: HTMLElement,
  tooltip: HTMLDivElement
) => {
  let left: string;
  let top: string;

  const tooltipWidth = tooltip.offsetWidth;
  const tooltipHeight = tooltip.offsetHeight;
  const tooltipHalfWidth = tooltipWidth / 2;

  const wrappedElement = associatedElement.childNodes?.[0] as HTMLElement;
  const associatedElementRect = wrappedElement?.getBoundingClientRect();

  if (!associatedElementRect || !wrappedElement) return;

  const centreOfAssociatedElement =
    associatedElementRect.left + associatedElementRect.width / 2;
  const scrollPosition = window.pageYOffset;

  const isTooltipOnRight = centreOfAssociatedElement < tooltipHalfWidth;
  const isTooltipOnLeft =
    tooltipHalfWidth > document.body.clientWidth - centreOfAssociatedElement;

  if (isTooltipOnRight) {
    // a centre align will cut off the tooltip on the left
    // so left align it
    left = `${centreOfAssociatedElement -
      TOOLTIP_TAIL_OFFSET -
      TOOLTIP_TAIL_WIDTH / 2}px`;
  } else if (isTooltipOnLeft) {
    // a centre align will cut off the tooltip on the right
    // so right align it
    left = `${centreOfAssociatedElement -
      tooltipWidth +
      +TOOLTIP_TAIL_OFFSET +
      TOOLTIP_TAIL_WIDTH / 2}px`;
  } else {
    // centre align
    left = `${centreOfAssociatedElement - tooltipHalfWidth}px`;
  }

  const topOfButton =
    associatedElementRect.top +
    scrollPosition -
    (tooltipHeight + TOOLTIP_MARGIN);

  // move tooltip to the bottom if cut off from above
  const isTooltipBelow = topOfButton <= scrollPosition + tooltipHeight;
  if (isTooltipBelow) {
    top = `${associatedElementRect.top +
      associatedElementRect.height +
      TOOLTIP_MARGIN +
      scrollPosition}px`;
  } else {
    top = `${topOfButton}px`;
  }

  const tailPosition = getTooltipTailPosition({
    isTooltipOnLeft,
    isTooltipOnRight,
    isTooltipBelow,
  });

  return {
    top,
    left,
    tailPosition,
  };
};

export const hideTooltip = () => {
  const tooltipElement = document.getElementById(TOOLTIP_ID) as HTMLDivElement;
  if (tooltipElement) {
    tooltipElement.style.visibility = 'hidden';
    tooltipElement.style.top = '0px';
  }
};

export const positionAndDisplayTooltip = (
  hoverText: string,
  tooltipWrapper: HTMLDivElement | null,
  options: {
    heading?: string;
    multiline?: boolean;
    secondaryHeading?: string;
    backgroundColour?: string;
    svgContent?: ReactNode;
  }
) => {
  const tooltipElement = document.getElementById(
    TOOLTIP_ID
  ) as HTMLDivElement | null;

  if (tooltipElement) {
    tooltipElement.style.backgroundColor = options.backgroundColour || grey9;
  }

  if (tooltipWrapper && tooltipElement) {
    removeAllChildren(tooltipElement);

    if (options.heading) {
      const heading = document.createElement('div');
      heading.innerText = options.heading;
      tooltipElement.appendChild(heading);

      if (options.secondaryHeading) {
        const headingSecondary = document.createElement('span');
        headingSecondary.innerText = options.secondaryHeading;
        heading.appendChild(headingSecondary);
      }
    }

    const contentContainer = document.createElement('div');
    contentContainer.style.display = 'flex';
    contentContainer.style.gap = '8px';

    const text = document.createElement('p');
    text.innerHTML = hoverText;
    contentContainer.appendChild(text);

    if (options.svgContent) {
      const svgContentContainer = document.createElement('div');
      contentContainer.appendChild(svgContentContainer);

      if (isValidElement(options.svgContent)) {
        const container = createRoot(svgContentContainer);
        container.render(options.svgContent);
      }
    }

    tooltipElement.appendChild(contentContainer);

    const position = getTooltipPosition(tooltipWrapper, tooltipElement);

    if (position) {
      tooltipElement.style.top = position.top;
      tooltipElement.style.left = position.left;
      tooltipElement.style.visibility = 'visible';
      tooltipElement.style.whiteSpace = options.multiline ? 'normal' : 'nowrap';
      tooltipElement.dataset.tailPosition = position.tailPosition;
      if (!options.multiline) {
        text.style.textOverflow = 'ellipsis';
        text.style.overflow = 'hidden';
      }
    }
  }
};

function removeAllChildren(element: HTMLElement) {
  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
}
